import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { proxyWache } from '../api';
import { format } from 'date-fns';

/**
 * Was die Proxy-Wache getan hat — und ein Knopf, um sie jetzt laufen zu lassen.
 *
 * Diese Ansicht ist kein Zubehoer. Eine Automatik, die auf fremden Wirten
 * Container neu startet, muss nachlesbar sein: wer sich fragt, warum eine
 * Seite um 03:19 kurz weg war, findet die Antwort sonst nirgends.
 */
export default function ProxyWachePanel() {
  const qc = useQueryClient();
  const [befunde, setBefunde] = useState<Awaited<ReturnType<typeof proxyWache.rundlauf>>['befunde'] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['proxy-wache', 'verlauf'],
    queryFn: proxyWache.verlauf,
  });

  const jetzt = useMutation({
    mutationFn: proxyWache.rundlauf,
    onSuccess: (r) => {
      setBefunde(r.befunde);
      qc.invalidateQueries({ queryKey: ['proxy-wache', 'verlauf'] });
    },
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-brand-600" />
            <h3 className="text-sm font-semibold text-slate-800">Proxy-Wache</h3>
          </div>
          <p className="mt-1 text-xs leading-snug text-slate-400">
            Prüft halbstündlich die Adressen aller aktiven Instanzen. Antwortet eine nicht, fragt
            der PC den Wirt, ob dessen Proxy den Namen überhaupt kennt — fehlt er, startet er den
            Abgleich-Beiwagen neu. Der Proxy selbst wird nie angefasst.
          </p>
        </div>
        <button
          onClick={() => jetzt.mutate()}
          disabled={jetzt.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          {jetzt.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Jetzt prüfen
        </button>
      </div>

      {befunde && (
        <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2">
          {befunde.length === 0 ? (
            <p className="text-xs text-emerald-700">
              Alle Adressen antworten. Kein Wirt wurde angefasst.
            </p>
          ) : (
            <ul className="space-y-1">
              {befunde.map((b, i) => (
                <li key={i} className="text-xs leading-snug text-slate-600">
                  <span className="font-mono text-slate-800">{b.adresse}</span>{' '}
                  <span className="text-slate-400">
                    (HTTP {b.code ?? 'kein Anschluss'}
                    {b.imProxy === false && ', nicht im Proxy'})
                  </span>{' '}
                  — {b.ergebnis}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Was der PC von selbst repariert hat
        </h4>
        {isLoading ? (
          <div className="mt-2 h-12 animate-pulse rounded bg-slate-100" />
        ) : !data?.eintraege.length ? (
          <p className="mt-2 text-xs text-slate-400">
            Noch nichts. Hier steht jede Reparatur — auch die erfolglosen.
          </p>
        ) : (
          <div className="mt-2 divide-y divide-slate-100">
            {data.eintraege.map((e) => (
              <div key={e.id} className="flex items-start gap-2 py-2">
                {e.erfolg ? (
                  <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-600" />
                ) : (
                  <XCircle size={13} className="mt-0.5 shrink-0 text-red-500" />
                )}
                <div className="min-w-0">
                  <div className="text-xs text-slate-700">
                    <span className="font-medium">{e.server.hostname}</span>
                    <span className="text-slate-400"> · {e.containerName} neu gestartet</span>
                  </div>
                  <div className="truncate font-mono text-xs text-slate-400">{e.ausloeser}</div>
                  <div className="text-xs text-slate-400">
                    {format(new Date(e.createdAt), 'dd.MM.yy HH:mm')} — {e.befundNachher}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {data && (
          <p className="mt-2 text-xs text-slate-400">
            Nach einer Reparatur bleibt derselbe Wirt {data.abkuehlzeitMinuten} Minuten
            unangetastet — ein Fehler, der sich nicht beheben lässt, soll keine Dauerschleife
            werden.
          </p>
        )}
      </div>
    </div>
  );
}
